/**
 * Records in the ⌘K palette: anything in the workspace matching what was typed,
 * each with an icon and a line saying what kind of thing it is and where it
 * lives. Choosing one opens it.
 */
import { useNavigate } from '@tanstack/react-router'
import {
  Briefcase,
  Building2,
  FileText,
  FolderKanban,
  Loader2,
  type LucideIcon,
  Mail,
  MapPin,
  Package,
  Receipt,
  User,
  UsersRound,
} from 'lucide-react'
import { CommandGroup, CommandItem } from '@/components/ui/command'
import { type SearchHit, type SearchKind, searchKinds } from '@/core/search'
import { useSearch } from '../api'

const icons: Record<SearchKind, LucideIcon> = {
  page: FileText,
  item: FolderKanban,
  job: Briefcase,
  client: Building2,
  location: MapPin,
  cleaner: UsersRound,
  invoice: Receipt,
  order: Package,
  user: User,
  invitation: Mail,
}

/** Headings, in the order the groups appear. Invitations sit with users. */
const groups: { heading: string; kinds: SearchKind[] }[] = [
  { heading: 'Pages', kinds: ['page'] },
  { heading: 'Clients', kinds: ['client', 'location'] },
  { heading: 'Jobs', kinds: ['job'] },
  { heading: 'Cleaners', kinds: ['cleaner'] },
  { heading: 'Invoices', kinds: ['invoice'] },
  { heading: 'Orders', kinds: ['order'] },
  { heading: 'Projects', kinds: ['item'] },
  { heading: 'Users', kinds: ['user', 'invitation'] },
]

export function SearchResults({ query, onOpen }: { query: string; onOpen: () => void }) {
  const navigate = useNavigate()
  const { data, isFetching, isError } = useSearch(query)
  if (!query.trim()) return null

  const hits = data ?? []
  const open = (hit: SearchHit) => {
    onOpen()
    void navigate({ href: hit.url })
  }

  return (
    <>
      {groups.map(({ heading, kinds }) => {
        const inGroup = hits.filter((hit) => kinds.includes(hit.kind))
        if (inGroup.length === 0) return null
        return (
          <CommandGroup key={heading} heading={heading}>
            {inGroup.map((hit) => {
              const Icon = icons[hit.kind]
              return (
                <CommandItem
                  key={`${hit.kind}:${hit.id}`}
                  // The server already matched these; the query as a keyword
                  // stops the palette's own filter from hiding them again.
                  value={`record:${hit.kind}:${hit.id}`}
                  keywords={[query]}
                  onSelect={() => open(hit)}
                >
                  <Icon className="size-4" aria-hidden="true" />
                  {/* On a phone the grey detail goes under the name, so neither is cut short. */}
                  <span className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-center sm:gap-2">
                    <span className="min-w-0 truncate sm:flex-1">{hit.title}</span>
                    <span className="min-w-0 truncate text-sm text-muted-foreground sm:ml-auto sm:max-w-[45%]">
                      <span className="sr-only">{searchKinds[hit.kind]}, </span>
                      {hit.parent}
                    </span>
                  </span>
                </CommandItem>
              )
            })}
          </CommandGroup>
        )
      })}
      {isFetching && hits.length === 0 ? (
        <CommandItem value="record:searching" keywords={[query]} disabled>
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Searching…
        </CommandItem>
      ) : null}
      {isError ? (
        <CommandItem value="record:error" keywords={[query]} disabled>
          Search is unavailable right now.
        </CommandItem>
      ) : null}
    </>
  )
}
